/**
 * Utility functions for handling markdown content in chat messages
 */

/**
 * Interface for image data extracted from markdown
 */
interface ImageData {
  src: string;
  alt: string;
  caption?: string;
}

/**
 * Interface for tech icon data extracted from markdown
 */
interface TechIconData {
  name: string;
  src: string;
  alt: string;
}

/**
 * Extract all images from markdown content
 * 
 * @param markdown - The markdown string to parse
 * @returns Array of image data objects
 */
export const extractImagesFromMarkdown = (markdown: string): ImageData[] => {
  const images: ImageData[] = [];
  
  // Regular expression to match markdown image syntax: ![alt text](url "optional title")
  const imageRegex = /!\[([^\]]*)\]\(([^)\s]+)(?:\s+"([^"]*)")?\)/g;
  let match;
  
  while ((match = imageRegex.exec(markdown)) !== null) {
    const alt = match[1] || '';
    const src = match[2];
    const caption = match[3] || '';
    
    if (src) {
      images.push({ src, alt, caption });
    }
  }
  
  return images;
};

/**
 * Remove image markdown syntax from text but preserve the alt text
 * 
 * @param markdown - The markdown string to clean
 * @returns Markdown string with image syntax removed
 */
export const stripImageMarkdown = (markdown: string): string => {
  // Replace ![alt](url "title") with alt text
  return markdown.replace(/!\[([^\]]*)\]\([^)]+(?:\s+"[^"]*")?\)/g, '$1');
};

/**
 * Check if content is likely about projects
 * 
 * @param content - The text content to check
 * @returns Boolean indicating if content is project-related
 */
export const isProjectContent = (content: string): boolean => {
  const projectKeywords = [
    'project', 'portfolio', 'application', 'app', 'development', 
    'developed', 'built', 'created', 'github', 'repository', 'demo'
  ];
  
  const lowerContent = content.toLowerCase();
  return projectKeywords.some(keyword => lowerContent.includes(keyword));
};

/**
 * Check if content is likely about skills
 * 
 * @param content - The text content to check
 * @returns Boolean indicating if content is skills-related
 */
export const isSkillsContent = (content: string): boolean => {
  const skillsKeywords = [
    'skill', 'technology', 'tech stack', 'programming', 'language',
    'framework', 'library', 'tool', 'proficient', 'expertise',
    'experience', 'knowledge', 'frontend', 'backend', 'fullstack',
    'development', 'coding', 'software', 'engineering', 'developer'
  ];
  
  const lowerContent = content.toLowerCase();
  return skillsKeywords.some(keyword => lowerContent.includes(keyword));
};

/**
 * Extract tech icons from markdown
 * This function handles both formats:
 * 1. Technology ![Alt](icon-url) - Traditional format with technology name preceding icon
 * 2. ![Alt](icon-url) - Standalone icon that might be a technology
 * 
 * @param markdown - The markdown string to parse
 * @returns Array of tech icon data objects
 */
export const extractTechIcons = (markdown: string): TechIconData[] => {
  const icons: TechIconData[] = [];
  
  // Pattern 1: Technology name followed by icon - "JavaScript ![JavaScript](url)"
  const namedIconRegex = /(\w+)\s*!\[([^\]]*)\]\(([^)]+)\)/g;
  let match;
  
  while ((match = namedIconRegex.exec(markdown)) !== null) {
    const name = match[1];
    const alt = match[2] || name;
    const src = match[3];
    
    if (src) {
      // Check if URL is likely an icon (SVG or small image file)
      if (isLikelyIconUrl(src)) {
        icons.push({ name, src, alt });
      }
    }
  }
  
  // Pattern 2: Standalone icons - "![React](https://example.com/react-icon.svg)"
  // For these, we use the alt text as the name
  const standaloneIconRegex = /!\[([^\]]+)\]\(([^)]+)\)/g;
  
  while ((match = standaloneIconRegex.exec(markdown)) !== null) {
    // Skip if this was already captured by the first pattern
    const alt = match[1];
    const src = match[2];
    const name = alt; // Use alt text as the name for standalone icons
    
    // Check if this icon hasn't been added yet and URL is likely an icon
    if (src && isLikelyIconUrl(src) && 
        !icons.some(icon => icon.src === src)) {
      icons.push({ name, src, alt });
    }
  }
  
  return icons;
};

/**
 * Helper function to determine if a URL is likely an icon
 * 
 * @param url - The URL to check
 * @returns Boolean indicating if the URL is likely an icon
 */
const isLikelyIconUrl = (url: string): boolean => {
  // Check for common icon file extensions
  if (/\.(svg|png|ico|webp)$/i.test(url)) {
    return true;
  }
  
  // Check for URLs from common icon hosting services
  const iconHostingServices = [
    'simpleicons.org',
    'shields.io',
    'img.shields.io',
    'cdn.jsdelivr.net/gh/devicons',
    'raw.githubusercontent.com',
    'devicon',
    'icon',
    'logo',
    'badge'
  ];
  
  return iconHostingServices.some(service => url.includes(service));
};

/**
 * Convert plain URLs in text to clickable HTML links
 * Can replace react-markdown for simple link rendering
 * 
 * @param text - Plain text that may contain URLs
 * @param targetBlank - Whether links should open in new tab (default: true)
 * @param className - Optional CSS class to add to link elements
 * @returns HTML string with clickable links
 */
export const convertLinksToHtml = (
  text: string, 
  targetBlank: boolean = true, 
  className: string = ''
): string => {
  if (!text) return '';
  
  // First handle markdown links: [text](url)
  const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  text = text.replace(markdownLinkRegex, (match, linkText, url) => {
    const target = targetBlank ? ' target="_blank" rel="noopener noreferrer"' : '';
    const classAttr = className ? ` class="${className}"` : '';
    return `<a href="${url}"${target}${classAttr}>${linkText}</a>`;
  });
  
  // Then handle plain URLs
  // This regex matches URLs starting with http://, https://, or www.
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
  
  return text.replace(urlRegex, (url) => {
    const href = url.startsWith('www.') ? `https://${url}` : url;
    const target = targetBlank ? ' target="_blank" rel="noopener noreferrer"' : '';
    const classAttr = className ? ` class="${className}"` : '';
    return `<a href="${href}"${target}${classAttr}>${url}</a>`;
  });
};

/**
 * Safely convert text with links to HTML without using dangerouslySetInnerHTML
 * by splitting text into an array of strings and link elements
 * 
 * @param text - Text that may contain URLs
 * @param targetBlank - Whether links should open in new tab (default: true)
 * @param className - Optional CSS class to add to link elements
 * @returns Array of strings and link render functions to use with React
 */
export const convertLinksToElements = (
  text: string,
  targetBlank: boolean = true,
  className: string = ''
): (string | { type: 'link', url: string, text: string, id: string })[] => {
  if (!text) return [''];
  
  const parts: (string | { type: 'link', url: string, text: string, id: string })[] = [];
  let lastIndex = 0;
  let linkId = 0;
  
  // First handle markdown links: [text](url)
  const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let markdownMatch;
  
  while ((markdownMatch = markdownLinkRegex.exec(text)) !== null) {
    // Add text before the link
    if (markdownMatch.index > lastIndex) {
      parts.push(text.substring(lastIndex, markdownMatch.index));
    }
    
    // Add the link
    parts.push({
      type: 'link',
      url: markdownMatch[2],
      text: markdownMatch[1],
      id: `link-${linkId++}`
    });
    
    lastIndex = markdownMatch.index + markdownMatch[0].length;
  }
  
  // Reset and handle plain URLs
  if (lastIndex === 0) {
    lastIndex = 0;
    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
    let urlMatch;
    
    while ((urlMatch = urlRegex.exec(text)) !== null) {
      // Add text before the link
      if (urlMatch.index > lastIndex) {
        parts.push(text.substring(lastIndex, urlMatch.index));
      }
      
      const url = urlMatch[0];
      const href = url.startsWith('www.') ? `https://${url}` : url;
      
      // Add the link
      parts.push({
        type: 'link',
        url: href,
        text: url,
        id: `link-${linkId++}`
      });
      
      lastIndex = urlMatch.index + urlMatch[0].length;
    }
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }
  
  return parts.length > 0 ? parts : [text];
};